import { PortableTextBlockComponent } from '@portabletext/react'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useWindowDimensions } from '../hooks/useWindowDimensions'
import {
  PortableTextBodyElement,
  portableTextToPlainText,
} from '../utilities/blockContent'
import { isNullOrWhiteSpace, sanitizeHtmlAndReplaceNewlines } from '../utilities/string'
import { DirectoryColumnDefinition, DirectoryList, DirectoryRow } from './DirectoryList'
import { numberSorter, stringSorter } from './DirectoryList/DirectoryList'
import { Grid } from './Grid'
import { MarketSection } from './MarketSection'
import { RichTextArea } from './PortableText/RichTextArea'

export interface SharesTableCell {
  content: PortableTextBodyElement[]
}

export interface SharesTableRow {
  cells: SharesTableCell[]
}

export interface SharesTableProps {
  footnotes?: string[]
  id?: string
  rows: SharesTableRow[]
  title?: string
  enableSort: boolean
}

const PlainBlockSerializer: PortableTextBlockComponent = ({ children }) => <>{children}</>

export const SharesTable = ({
  footnotes,
  id,
  rows,
  title,
  enableSort,
}: SharesTableProps) => {
  const { documentClientWidth } = useWindowDimensions()

  const nonHeaderRows = useMemo(() => rows.slice(1), [rows])

  const columnDefinitions: DirectoryColumnDefinition[] = useMemo(
    () =>
      rows[0]?.cells.map((headerCell, cellIndex) => {
        const isString = nonHeaderRows.every(row =>
          isNaN(parseFloat(portableTextToPlainText(row.cells[cellIndex].content)))
        )

        const plainText = portableTextToPlainText(headerCell.content, '')

        return {
          name: !isNullOrWhiteSpace(plainText) ? plainText : uuidv4(),
          render: (value: PortableTextBodyElement[]) => (
            <RichTextArea components={{ block: PlainBlockSerializer }} value={value} />
          ),
          renderHeader: (
            <RichTextArea
              components={{ block: PlainBlockSerializer }}
              value={headerCell.content}
            />
          ),
          sort: (
            left: PortableTextBodyElement[],
            right: PortableTextBodyElement[],
            asc: boolean
          ) => {
            const leftValue = portableTextToPlainText(left, '')
            const rightValue = portableTextToPlainText(right, '')

            return isString
              ? stringSorter(leftValue, rightValue, asc)
              : numberSorter(leftValue, rightValue, asc)
          },
        }
      }) ?? [],
    [nonHeaderRows, rows]
  )

  const directoryRows: DirectoryRow[] = useMemo(
    () =>
      nonHeaderRows.map(row => ({
        columns: row.cells.map((cell, index) => ({
          name: columnDefinitions[index]?.name,
          value: cell.content,
        })),
        key: uuidv4(),
      })),
    [columnDefinitions, nonHeaderRows]
  )

  return (
    <MarketSection applyGrid={false} id={id}>
      <div
        className="flex flex-col gap-30"
        style={{
          '--document-width':
            documentClientWidth === 0 ? '100vw' : `${documentClientWidth}px`,
        }}
      >
        <div
          className={classNames(
            // Use calculated paddings to support horizontal scroll while simulating grid widths
            // Use the --document-width variable set above instead of 100vw, to account for the
            // scrollbar.
            'overflow-auto hide-scrollbar px-[calc((var(--document-width)-330px)/2)]',
            'tb:px-[calc((var(--document-width)-615px)/2)]',
            'sm:px-[calc((var(--document-width)-850px)/2)]',
            'lg:px-[calc((var(--document-width)-1050px)/2)]',
            'xl:px-[calc((var(--document-width)-1410px)/2)]',
            'xxl:px-[calc((var(--document-width)-1770px)/2)]'
          )}
        >
          <div className="flex flex-col min-w-fit p-30 gap-30 rounded-15 border border-primary sm:p-45">
            {!isNullOrWhiteSpace(title) && (
              <h2
                className="font-h2 text-h2 text-secondary-dark"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlAndReplaceNewlines(title ?? ''),
                }}
              />
            )}

            <DirectoryList
              arrowSizeClassName="w-20 h-20"
              classNameOverrides={{
                listClassName: '',
                headerRowClassName:
                  'grid grid-flow-col auto-cols-[minmax(180px,1fr)] min-w-max py-15 gap-75 border-b border-neutral-light',
                headerCellClassName: classNames(
                  'cursor-pointer text-secondary-dark',
                  'text-t-600',
                  'sm:text-t-700'
                ),
                rowClassName: 'flex py-15 gap-75 border-b border-neutral-light',
                cellClassName: classNames('flex-1 text-secondary-dark', 'text-t-400'),
              }}
              collapseOnMobile={false}
              columnDefinitions={columnDefinitions}
              rows={directoryRows}
              sortInitially={false}
              enableSort={enableSort}
            />
          </div>
        </div>

        {!!footnotes?.length && (
          <Grid>
            <ol
              className={classNames(
                'col-span-full flex flex-col gap-15 font-body text-body text-secondary-darker list-decimal',
                'tb:mx-45',
                'sm:col-start-2 sm:col-span-4 sm:-mx-75',
                'lg:col-start-2 lg:col-span-6 lg:mx-0',
                'xl:col-start-2 xl:col-span-8',
                'xxl:col-start-3 xxl:col-span-10'
              )}
            >
              {footnotes.map((footnote, index) => (
                <li className="ml-[1em]" key={index}>
                  {footnote}
                </li>
              ))}
            </ol>
          </Grid>
        )}
      </div>
    </MarketSection>
  )
}
